/**
 * Wire Tracker Page - overview across all rounds.
 * Lists commitments with non-confirmed wire status for back-office tracking.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Banknote,
  CheckCircle2,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import syndicationService from '../services/syndicationService';
import {
  SyndicationRound,
  SyndicationCommitment,
  WireStatus,
} from '../types/syndication';
import WireStatusStepper from '../components/WireStatusStepper';
import { formatCurrency } from './SyndicationPage';

type WireRow = {
  commitment: SyndicationCommitment;
  round: SyndicationRound;
};

type SortKey = 'amount' | 'status' | 'wire_date' | 'round';

const WIRE_STATUSES: Array<'' | WireStatus> = [
  '',
  'pending',
  'initiated',
  'received',
  'confirmed',
  'refunded',
];

const formatDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString() : '—';

const WireTrackerPage: React.FC = () => {
  const [rows, setRows] = useState<WireRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [statusFilter, setStatusFilter] = useState<'' | WireStatus>('');
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortAsc, setSortAsc] = useState(true);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const load = async () => {
    setLoading(true);
    const roundsRes = await syndicationService.fetchRounds({ size: 200 });
    if (roundsRes.error) {
      toast.error(roundsRes.error);
      setLoading(false);
      return;
    }
    const allRounds = roundsRes.data?.rounds || [];
    // Fetch full details (with commitments) in parallel
    const details = await Promise.all(
      allRounds.map((r) => syndicationService.fetchRound(r.id))
    );
    const newRows: WireRow[] = [];
    details.forEach((d) => {
      if (d.data && d.data.commitments) {
        d.data.commitments.forEach((c) => {
          if (c.wire_status !== 'confirmed') {
            newRows.push({ commitment: c, round: d.data! });
          }
        });
      }
    });
    setRows(newRows);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filteredSorted = useMemo(() => {
    let list = rows;
    if (statusFilter !== '') {
      list = list.filter((r) => r.commitment.wire_status === statusFilter);
    }
    const sorted = [...list].sort((a, b) => {
      let diff = 0;
      switch (sortKey) {
        case 'amount':
          diff =
            (a.commitment.allocated_amount || a.commitment.amount) -
            (b.commitment.allocated_amount || b.commitment.amount);
          break;
        case 'status':
          diff = a.commitment.wire_status.localeCompare(b.commitment.wire_status);
          break;
        case 'wire_date':
          diff = (a.commitment.wire_date || '').localeCompare(
            b.commitment.wire_date || ''
          );
          break;
        case 'round':
          diff = a.round.round_name.localeCompare(b.round.round_name);
          break;
      }
      return sortAsc ? diff : -diff;
    });
    return sorted;
  }, [rows, statusFilter, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const toggleSelect = (commitmentId: number) => {
    const next = new Set(selected);
    if (next.has(commitmentId)) next.delete(commitmentId);
    else next.add(commitmentId);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredSorted.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredSorted.map((r) => r.commitment.id)));
    }
  };

  const bulkMarkReceived = async () => {
    if (selected.size === 0) {
      toast.error('Select commitments to mark');
      return;
    }
    if (
      !window.confirm(`Mark ${selected.size} wire(s) as received?`)
    )
      return;
    setBulkUpdating(true);
    const ids = Array.from(selected);
    const today = new Date().toISOString().split('T')[0];
    const results = await Promise.all(
      ids.map((id) =>
        syndicationService.updateWire(id, {
          status: 'received',
          wire_date: today,
        })
      )
    );
    setBulkUpdating(false);
    const errors = results.filter((r) => r.error).length;
    if (errors > 0) {
      toast.error(`${errors} update(s) failed`);
    } else {
      toast.success(`${ids.length} wire(s) marked received`);
    }
    setSelected(new Set());
    await load();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/syndication"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to syndication
        </Link>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
              <Banknote className="w-6 h-6 mr-2 text-blue-600" />
              Wire Tracker
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              All pending and in-flight wires across every syndication round.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as '' | WireStatus)
              }
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {WIRE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === '' ? 'All non-confirmed' : s}
                </option>
              ))}
            </select>
            <button
              onClick={bulkMarkReceived}
              disabled={selected.size === 0 || bulkUpdating}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {bulkUpdating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Mark received ({selected.size})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading wires...
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <Banknote className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p>No pending wires. All settled.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={
                        filteredSorted.length > 0 &&
                        selected.size === filteredSorted.length
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Investor
                  </th>
                  <th
                    className="text-left px-3 py-2 font-medium text-gray-700 cursor-pointer hover:text-blue-600"
                    onClick={() => toggleSort('round')}
                  >
                    Round {sortKey === 'round' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th
                    className="text-right px-3 py-2 font-medium text-gray-700 cursor-pointer hover:text-blue-600"
                    onClick={() => toggleSort('amount')}
                  >
                    Amount {sortKey === 'amount' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th
                    className="text-left px-3 py-2 font-medium text-gray-700 cursor-pointer hover:text-blue-600"
                    onClick={() => toggleSort('status')}
                  >
                    Status {sortKey === 'status' && (sortAsc ? '↑' : '↓')}
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Reference
                  </th>
                  <th
                    className="text-left px-3 py-2 font-medium text-gray-700 cursor-pointer hover:text-blue-600"
                    onClick={() => toggleSort('wire_date')}
                  >
                    Wire date {sortKey === 'wire_date' && (sortAsc ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSorted.map(({ commitment: c, round: r }) => (
                  <tr key={c.id}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">
                        {c.investor?.full_name || `Investor #${c.investor_id}`}
                      </div>
                      {c.investor?.firm_name && (
                        <div className="text-xs text-gray-500">
                          {c.investor.firm_name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/syndication/${r.id}`}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        {r.round_name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatCurrency(c.allocated_amount || c.amount)}
                    </td>
                    <td className="px-3 py-2">
                      <WireStatusStepper status={c.wire_status} compact />
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {c.wire_reference || '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {formatDate(c.wire_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WireTrackerPage;
